import { on } from '@ngrx/store';

import { emptyState, errorState, loadingState, resultState } from '../../@core/fetch-state.utils';
import { AppState } from '../app.state.interface';

import { QuotesActions } from './quotes.actions';
import { QuotesState } from './quotes.state.interface';

export const initialState: QuotesState = {
  activeQuotes: emptyState(),
  activeQuotesTotalCount: 0,
  archivedQuotes: emptyState(),
  archivedQuotesTotalCount: 0,
  assignees: emptyState(),
  customers: emptyState(),
  statuses: emptyState(),
  shippingMethods: emptyState(),
  quotesShippingMethod: emptyState(),
  caNumbers: emptyState(),
  selectedCANumber: emptyState(),
  itemProcesses: emptyState(),
  selectedItemProcess: emptyState(),
  createdQuote: emptyState(),
  selectedQuote: emptyState(),
  itemProcessesCategories: emptyState(),
  savedNote: emptyState(),
  exportQuotesBundle: emptyState(),
  archivedQuote: emptyState(),
  unarchivedQuote: emptyState(),
  deletedQuote: emptyState(),
  updatedQuote: emptyState(),
  duplicatedQuote: emptyState(),
};

export const quotesStateReducer = [
  // Load Active Quotes
  on(QuotesActions.loadActiveQuotes, (state: AppState) => ({
    ...state,
    activeQuotes: loadingState(),
    activeQuotesTotalCount: 0,
  })),
  on(QuotesActions.loadActiveQuotesSuccess, (state: AppState, { activeQuotes, totalCount }) => ({
    ...state,
    activeQuotes: resultState(activeQuotes),
    activeQuotesTotalCount: totalCount,
  })),
  on(QuotesActions.loadActiveQuotesFailure, (state: AppState, { error }) => ({
    ...state,
    activeQuotes: errorState([error]),
    activeQuotesTotalCount: 0,
  })),

  // Load Archived Quotes
  on(QuotesActions.loadArchivedQuotes, (state: AppState) => ({
    ...state,
    archivedQuotes: loadingState(),
    archivedQuotesTotalCount: 0,
  })),
  on(
    QuotesActions.loadArchivedQuotesSuccess,
    (state: AppState, { archivedQuotes, totalCount }) => ({
      ...state,
      archivedQuotes: resultState(archivedQuotes),
      archivedQuotesTotalCount: totalCount,
    }),
  ),
  on(QuotesActions.loadArchivedQuotesFailure, (state: AppState, { error }) => ({
    ...state,
    archivedQuotes: errorState([error]),
    archivedQuotesTotalCount: 0,
  })),

  // Load Assignees
  on(QuotesActions.loadAssignees, (state: AppState) => ({
    ...state,
    assignees: loadingState(),
  })),
  on(QuotesActions.loadAssigneesSuccess, (state: AppState, { assignees }) => ({
    ...state,
    assignees: resultState(assignees),
  })),
  on(QuotesActions.loadAssigneesFailure, (state: AppState, { error }) => ({
    ...state,
    assignees: errorState([error]),
  })),

  // Load Customers
  on(QuotesActions.loadCustomers, (state: AppState) => ({
    ...state,
    customers: loadingState(),
  })),
  on(QuotesActions.loadCustomersSuccess, (state: AppState, { customers }) => ({
    ...state,
    customers: resultState(customers),
  })),
  on(QuotesActions.loadCustomersFailure, (state: AppState, { error }) => ({
    ...state,
    customers: errorState([error]),
  })),

  // Load Statuses
  on(QuotesActions.loadStatuses, (state: AppState) => ({
    ...state,
    statuses: loadingState(),
  })),
  on(QuotesActions.loadStatusesSuccess, (state: AppState, { statuses }) => ({
    ...state,
    statuses: resultState(statuses),
  })),
  on(QuotesActions.loadStatusesFailure, (state: AppState, { error }) => ({
    ...state,
    statuses: errorState([error]),
  })),

  // Load Quote Shipping Method
  on(QuotesActions.loadQuoteShippingMethod, (state: AppState) => ({
    ...state,
    quotesShippingMethod: loadingState(),
  })),
  on(QuotesActions.loadQuoteShippingMethodSuccess, (state: AppState, { quotesShippingMethod }) => ({
    ...state,
    quotesShippingMethod: resultState(quotesShippingMethod),
  })),
  on(QuotesActions.loadQuoteShippingMethodFailure, (state: AppState, { error }) => ({
    ...state,
    quotesShippingMethod: errorState([error]),
  })),

  // Load Shipping Methods
  on(QuotesActions.loadShippingMethods, (state: AppState) => ({
    ...state,
    shippingMethods: loadingState(),
  })),
  on(QuotesActions.loadShippingMethodsSuccess, (state: AppState, { shippingMethods }) => ({
    ...state,
    shippingMethods: resultState(shippingMethods),
  })),
  on(QuotesActions.loadShippingMethodsFailure, (state: AppState, { error }) => ({
    ...state,
    shippingMethods: errorState([error]),
  })),

  // Load CA Numbers
  on(QuotesActions.loadCANumbers, (state: AppState) => ({
    ...state,
    caNumbers: loadingState(),
  })),
  on(QuotesActions.loadCANumbersSuccess, (state: AppState, { caNumbers }) => ({
    ...state,
    caNumbers: resultState(caNumbers),
  })),
  on(QuotesActions.loadCANumbersFailure, (state: AppState, { error }) => ({
    ...state,
    caNumbers: errorState([error]),
  })),

  // Load CA Number By Id
  on(QuotesActions.loadCANumberById, (state: AppState) => ({
    ...state,
    selectedCANumber: loadingState(),
  })),
  on(QuotesActions.loadCANumberByIdSuccess, (state: AppState, { caNumberById }) => ({
    ...state,
    selectedCANumber: resultState(caNumberById),
  })),
  on(QuotesActions.loadCANumberByIdFailure, (state: AppState, { error }) => ({
    ...state,
    selectedCANumber: errorState([error]),
  })),

  // Clear Selected CANumber
  on(QuotesActions.clearSelectedCANumber, (state: AppState) => ({
    ...state,
    selectedCANumber: emptyState(),
  })),

  // Load All Item Processes
  on(QuotesActions.loadItemProcesses, (state: AppState) => ({
    ...state,
    itemProcesses: loadingState(),
  })),
  on(QuotesActions.loadItemProcessesSuccess, (state: AppState, { itemProcesses }) => ({
    ...state,
    itemProcesses: resultState(itemProcesses),
  })),
  on(QuotesActions.loadItemProcessesFailure, (state: AppState, { error }) => ({
    ...state,
    itemProcesses: errorState([error]),
  })),

  // Load Specific Item Process
  on(QuotesActions.loadSpecificItemProcess, (state: AppState) => ({
    ...state,
    selectedItemProcess: loadingState(),
  })),
  on(QuotesActions.loadSpecificItemProcessSuccess, (state: AppState, { selectedItemProcess }) => ({
    ...state,
    selectedItemProcess: resultState(selectedItemProcess),
  })),
  on(QuotesActions.loadSpecificItemProcessFailure, (state: AppState, { error }) => ({
    ...state,
    selectedItemProcess: errorState([error]),
  })),

  // Create Quote
  on(QuotesActions.createQuote, (state: AppState) => ({
    ...state,
    createdQuote: loadingState(),
  })),
  on(QuotesActions.createQuoteSuccess, (state: AppState, { createdQuote }) => ({
    ...state,
    createdQuote: resultState(createdQuote),
  })),
  on(QuotesActions.createQuoteFailure, (state: AppState, { error }) => ({
    ...state,
    createdQuote: errorState([error]),
  })),

  // Update Quote
  on(QuotesActions.updateQuote, (state: AppState) => ({
    ...state,
    updatedQuote: loadingState(),
  })),
  on(QuotesActions.updateQuoteSuccess, (state: AppState, { quote }) => ({
    ...state,
    updatedQuote: resultState(quote),
  })),
  on(QuotesActions.updateQuoteFailure, (state: AppState, { error }) => ({
    ...state,
    updatedQuote: errorState([error]),
  })),

  // Load Specific Quote
  on(QuotesActions.loadSpecificQuote, (state: AppState) => ({
    ...state,
    selectedQuote: loadingState(),
  })),
  on(QuotesActions.loadSpecificQuoteSuccess, (state: AppState, { selectedQuote }) => ({
    ...state,
    selectedQuote: resultState(selectedQuote),
  })),
  on(QuotesActions.loadSpecificQuoteFailure, (state: AppState, { error }) => ({
    ...state,
    selectedQuote: errorState([error]),
  })),

  // Clear Selected Quote
  on(QuotesActions.clearSelectedQuote, (state: AppState) => ({
    ...state,
    selectedQuote: emptyState(),
  })),

  // Load Item Processes Categories
  on(QuotesActions.loadItemProcessesCategories, (state: AppState) => ({
    ...state,
    itemProcessesCategories: loadingState(),
  })),
  on(
    QuotesActions.loadItemProcessesCategoriesSuccess,
    (state: AppState, { itemProcessesCategories }) => ({
      ...state,
      itemProcessesCategories: resultState(itemProcessesCategories),
    }),
  ),
  on(QuotesActions.loadItemProcessesCategoriesFailure, (state: AppState, { error }) => ({
    ...state,
    itemProcessesCategories: errorState([error]),
  })),

  // Clear Item Process State
  on(QuotesActions.clearItemProcessState, (state: AppState) => ({
    ...state,
    selectedItemProcess: emptyState(),
  })),

  // Save Note
  on(QuotesActions.saveNote, (state: AppState) => ({
    ...state,
    savedNote: loadingState(),
  })),
  on(QuotesActions.saveNoteSuccess, (state: AppState, { savedNote }) => ({
    ...state,
    savedNote: resultState(savedNote.notes),
  })),
  on(QuotesActions.saveNoteFailure, (state: AppState, { error }) => ({
    ...state,
    savedNote: errorState([error]),
  })),

  on(QuotesActions.clearNote, (state: AppState) => ({
    ...state,
    savedNote: emptyState(),
  })),

  // Export Quotes Bundle
  on(QuotesActions.exportQuotesBundle, (state: AppState) => ({
    ...state,
    exportQuotesBundle: loadingState(),
  })),
  on(QuotesActions.exportQuotesBundleSuccess, (state: AppState, { response }) => ({
    ...state,
    exportQuotesBundle: resultState(response),
  })),
  on(QuotesActions.exportQuotesBundleFailure, (state: AppState, { error }) => ({
    ...state,
    exportQuotesBundle: errorState([error]),
  })),

  // Archive Quote
  on(QuotesActions.archiveQuote, (state: AppState) => ({
    ...state,
    archivedQuote: loadingState(),
  })),
  on(QuotesActions.archiveQuoteSuccess, (state: AppState, { archivedQuote }) => ({
    ...state,
    archivedQuote: resultState(archivedQuote),
  })),
  on(QuotesActions.archiveQuoteFailure, (state: AppState, { error }) => ({
    ...state,
    archivedQuote: errorState([error]),
  })),

  // Unarchive Quote
  on(QuotesActions.unarchiveQuote, (state: AppState) => ({
    ...state,
    unarchivedQuote: loadingState(),
  })),
  on(QuotesActions.unarchiveQuoteSuccess, (state: AppState, { unarchivedQuote }) => ({
    ...state,
    unarchivedQuote: resultState(unarchivedQuote),
  })),
  on(QuotesActions.unarchiveQuoteFailure, (state: AppState, { error }) => ({
    ...state,
    unarchivedQuote: errorState([error]),
  })),
  on(QuotesActions.deleteQuote, (state: AppState) => ({
    ...state,
    deletedQuote: loadingState(),
  })),
  on(QuotesActions.deleteQuoteSuccess, (state: AppState) => ({
    ...state,
    deletedQuote: resultState(''),
  })),
  on(QuotesActions.deleteQuoteFailure, (state: AppState, { error }) => ({
    ...state,
    deletedQuote: errorState([error]),
  })),

  // Duplicate Quote
  on(QuotesActions.duplicateQuote, (state: AppState) => ({
    ...state,
    duplicatedQuote: loadingState(),
  })),
  on(QuotesActions.duplicateQuoteSuccess, (state: AppState, { quoteId }) => ({
    ...state,
    duplicatedQuote: resultState(quoteId),
  })),
  on(QuotesActions.duplicateQuoteFailure, (state: AppState, { error }) => ({
    ...state,
    duplicatedQuote: errorState([error]),
  })),
];
